// frontend/src/components/TurnstileWidget.js
// Cloudflare Turnstile widget wrapper for React.
// Loads the Turnstile script lazily and renders the widget.
// Calls onVerify(token) when the user passes the challenge.

import { useEffect, useRef } from 'react';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const loadTurnstileScript = () => {
  if (document.getElementById(SCRIPT_ID)) return;
  const s = document.createElement('script');
  s.id = SCRIPT_ID;
  s.src = SCRIPT_SRC;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
};

/**
 * Cloudflare Turnstile widget.
 *
 * @param {Object} props
 * @param {string} props.siteKey - Turnstile site key (REACT_APP_TURNSTILE_SITE_KEY)
 * @param {(token: string) => void} props.onVerify - called when user passes
 * @param {() => void} [props.onExpire] - called when token expires (default: no-op)
 * @param {() => void} [props.onError] - called on widget error
 * @param {'light'|'dark'|'auto'} [props.theme]
 */
export default function TurnstileWidget({ siteKey, onVerify, onExpire, onError, theme = 'auto' }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!siteKey) {
      console.warn('TurnstileWidget: siteKey is missing. Set REACT_APP_TURNSTILE_SITE_KEY env var.');
      return;
    }

    loadTurnstileScript();

    let cancelled = false;

    const tryRender = () => {
      if (cancelled) return;
      if (!window.turnstile) {
        // Script not yet loaded; retry shortly.
        setTimeout(tryRender, 100);
        return;
      }
      if (!containerRef.current || widgetIdRef.current) return;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme,
        callback: (token) => onVerify?.(token),
        'expired-callback': () => onExpire?.(),
        'error-callback': () => onError?.(),
      });
    };

    tryRender();

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Already removed; ignore.
        }
        widgetIdRef.current = null;
      }
    };
    // siteKey is the only meaningful re-render trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  return <div ref={containerRef} />;
}
